// "Download report" for one test run, for the developer surface.
//
// The sheet is rebuilt server-side on every click and sent no-store, so it always
// reflects the CURRENT state of the run — the verdicts this developer just
// recorded, and any re-run the tester has made since. That is the whole point of
// downloading it from here rather than being emailed one when the batch finished:
// the emailed copy is a snapshot, this one is live.
//
// The result is reported out loud. A failed download used to be indistinguishable
// from a slow one: the spinner stopped, no file arrived, and nothing said why.
import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { toastError, toastSuccess } from "@/lib/toast";
import { downloadDevBatchReport } from "@/api/developer";

function prettySize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DownloadReportButton({
  kind,
  batchId,
  label = "Download report",
  variant = "outline",
  size = "sm",
  className,
  // Row-level use: an icon-only control inside a clickable table row.
  compact = false,
  disabled = false,
  disabledHint,
}) {
  const [busy, setBusy] = useState(false);

  async function download(e) {
    // The runs table opens the report on row click; downloading must not also
    // navigate away from the page the file is landing on.
    e?.stopPropagation?.();
    if (busy || disabled) return;
    setBusy(true);
    try {
      const { filename, sizeBytes } = await downloadDevBatchReport(kind, batchId);
      const size = prettySize(sizeBytes);
      toastSuccess(`Downloaded ${filename}${size ? ` (${size})` : ""} — current as of now.`);
    } catch (err) {
      toastError((err && err.message) || "That report couldn't be downloaded.");
    } finally {
      setBusy(false);
    }
  }

  if (compact) {
    return (
      <button
        type="button"
        onClick={download}
        disabled={busy || disabled}
        title={disabled ? (disabledHint || "Nothing to report yet") : "Download this run's report sheet (.xlsx)"}
        aria-label={`Download the report for this run`}
        className={cn(
          "grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors",
          "hover:bg-brand-50 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-40",
          "dark:hover:bg-brand-500/10 dark:hover:text-brand-300",
          className,
        )}
      >
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
      </button>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={download}
      disabled={busy || disabled}
      title={disabled ? (disabledHint || "Nothing to report yet") : "Rebuilt from the latest results every time"}
      className={className}
    >
      {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
      {busy ? "Preparing…" : label}
    </Button>
  );
}
